<!--
 @description Cookie consent banner with Accept All, Refuse All, and a
 "Manage preferences" flow that opens a full-screen Modal. The modal lets
 users toggle analytics and ads cookies individually; essential cookies are
 always on and disabled. Emits `close` with an `AcceptedCookies` payload
 whenever the user makes a final choice (accept all, refuse all, or save
 preferences). The parent is responsible for hiding this banner after `close`.

 @example
 <CookieBanner v-if="showBanner" @close="handleCookieConsent" />
-->
<template>
  <div class="cookie-banner banner-container">
    <p class="cookie-banner__text">{{ $t('cookies.banner.text-1') }}</p>
    <p class="cookie-banner__text">{{ $t('cookies.banner.text-2') }}</p>
    <div class="cookie-banner__actions">
      <button class="primary bold full-width" @click="refuseAll">{{ $t('global.refuse') }}</button>
      <button class="secondary bold full-width" @click="acceptAll">
        {{ $t('global.accept') }}
      </button>
      <button class="tertiary bold full-width" @click="cookieModalOpen = true">
        {{ $t('cookies.banner.manage-preferences') }}
      </button>
    </div>
  </div>
  <Modal :fullScreen="true" :isOpen="cookieModalOpen" @close="closeCookieModal">
    <div class="cookie-banner__modal">
      <div class="cookie-banner__modal-top">
        <h2 class="primary">{{ $t('cookies.modal.title') }}</h2>
        <p>{{ $t('cookies.modal.text') }}</p>
        <button class="secondary bold" @click="acceptAll">
          {{ $t('cookies.modal.accept-all') }}
        </button>
      </div>
      <div class="cookie-banner__modal-choices">
        <p class="cookie-banner__modal-control">{{ $t('cookies.modal.control') }}</p>
        <div class="cookie-banner__modal-switch">
          <Switch id="essentials" :modelValue="true" :disabled="true" />
          <p>
            <strong>{{ $t('cookies.modal.essentials.title') }}</strong>
            {{ $t('cookies.modal.essentials.text') }}
          </p>
        </div>
        <div class="cookie-banner__modal-switch">
          <Switch id="analythics" :modelValue="analythics" @update:modelValue="updateAnalythics" />
          <p>
            <strong>{{ $t('cookies.modal.analytics.title') }}</strong>
            {{ $t('cookies.modal.analytics.text') }}
          </p>
        </div>
        <div class="cookie-banner__modal-switch">
          <Switch id="ads" :modelValue="ads" @update:modelValue="updateAds" />
          <p>
            <strong>{{ $t('cookies.modal.ads.title') }}</strong>
            {{ $t('cookies.modal.ads.text') }}
          </p>
        </div>
      </div>
      <div class="cookie-banner__modal-actions">
        <button class="primary bold" @click="refuseAll">
          {{ $t('global.refuse') }}
        </button>
        <button class="tertiary bold" @click="closeCookieModal">
          {{ $t('cookies.modal.save') }}
        </button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/common/Global/modal/Modal.vue'
import { ref } from 'vue'
import Switch from '@/components/common/form/switch/Switch.vue'
import type { AcceptedCookies } from '@/types/Types'

const cookieModalOpen = ref(false)
const analythics = ref(false)
const ads = ref(false)
const acceptedCookies = ref<AcceptedCookies>({
  analythics: false,
  ads: false
})

const emit = defineEmits<{
  (event: 'close', acceptedCookies: AcceptedCookies): void
}>()

const closeCookieModal = () => {
  acceptedCookies.value = {
    analythics: analythics.value,
    ads: ads.value
  }

  emit('close', acceptedCookies.value)

  cookieModalOpen.value = false
}

const updateAnalythics = (value: boolean) => {
  analythics.value = value
}

const updateAds = (value: boolean) => {
  ads.value = value
}

const acceptAll = () => {
  analythics.value = true
  ads.value = true

  closeCookieModal()
}

const refuseAll = () => {
  analythics.value = false
  ads.value = false

  closeCookieModal()
}
</script>

<style scoped lang="scss">
.cookie-banner {
  @extend %flex-column;
  position: fixed;
  bottom: 0;
  background-color: var(--white);
  z-index: 100;
  padding: $triple-spacing;
  gap: $base-spacing;

  &__text {
    max-width: 800px;
    margin: 0;
  }

  &__actions {
    @extend %flex;
    justify-content: center;
    gap: $base-spacing;
    max-width: 800px;

    button {
      margin: 0;
    }

    @include breakpoint-md {
      flex-direction: column;
      gap: $base-spacing;
    }
  }

  &__modal {
    @extend %flex-column;
    justify-content: space-between;
    height: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: $triple-spacing 0;
  }

  &__modal-top {
    padding: 0 $base-spacing;

    h2 {
      text-align: center;
      margin-bottom: $double-spacing;
    }

    button {
      max-width: 150px;
      margin: $double-spacing 0;

      @include breakpoint-md {
        max-width: none;
      }
    }
  }

  &__modal-control {
    margin-left: 75px;

    @include breakpoint-md {
      margin-left: 0;
    }
  }

  &__modal-switch {
    @extend %flex;
  }

  &__modal-actions {
    @extend %flex;
    justify-content: space-between;
    gap: $base-spacing;
    margin-top: $double-spacing;

    button {
      margin: 0;
      width: 200px;
    }
  }
}
</style>
